import { Action } from '@reduxjs/toolkit';
import { isEmpty, prop } from 'ramda';
import { StateObservable, combineEpics } from 'redux-observable';
import {
  concat, of, Subject, from, Observable,
} from 'rxjs';
import {
  buffer,
  catchError,
  debounceTime,
  filter,
  first,
  flatMap,
  ignoreElements,
  map,
  pluck,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { webSocket } from 'rxjs/webSocket';

import { externalGameTypesSelector } from '@ezugi/bootstrap/src/store/selectors/externalGameTypes';
import {
  CANCEL_TABLE,
  INITIAL_DATA,
  INITIALIZE_TABLE,
  UPDATE_FREE_SEATS,
  UPDATED_LOBBY_DATA,
  UPDATED_TABLE_HISTORY,
  NAVIGATION_METHODS,
  TABLE_OCCUPANCY,
  SHUFFLE,
} from '@ezugi/constants';
import {
  buildQueryFromObject,
  getAuthEndpointForExternalIntegration,
} from '@ezugi/utils';

import {
  MINILOBBY_RESERVE_SEAT,
  MINILOBBY_PLAYERS_NUMBER_UPDATE,
} from '../../../constants/minilobby';
import { logError } from '../../../util/logUtils';
import { POST_MESSAGES } from '../../../util/postMessage';
import { lobbyActions, notificationActions } from '../../actions';
import {
  userSelector,
  authSelector,
  isEvoWrappedSelector,
  userIdSelector,
  currentLanguageSelector,
} from '../../selectors';
import {
  lobbyAuthGroupsSelector,
  lobbyLoadingSelector,
  lobbyOpenSelector,
  lobbyOpenTableMethod,
} from '../../selectors/lobby';
import { RootState } from '../../types';
import {
  ADD_FAVORITE,
  getLobbyPostFn, mapLobbyData, pluckSocketParams, redirectToGame,
  REMOVE_FAVORITE,
  setFavorite,
  updateAuthGroups,
} from './utils';

const { lobby } = lobbyActions;
const { notification } = notificationActions;

const VITE_IS_BUNDLE = import.meta.env.VITE_IS_BUNDLE;

const onErrorEventHandler = (error: string | Error) => {
  logError(error, 'error');

  return of(
    notification.add({
      message: 'notifications.failed_getting_lobby_data',
    }),
    lobby.close()
  );
};

const toggleEpic = (
  action$: Observable<Action>,
  state$: StateObservable<RootState>
) => action$.pipe(
  filter(lobby.toggle.match),
  map(() => (lobbyOpenSelector(state$.value) ? lobby.close() : lobby.open()))
);

const openEpic = (
  action$: Observable<Action>,
  state$: StateObservable<RootState>
): Observable<Action> => action$.pipe(
  filter(
    (action) => lobby.open.type === action.type
        || lobby.openMenuWidget.type === action.type
  ),
  filter(() => !lobbyLoadingSelector(state$.value)),
  switchMap(() => {
    const openEvents = new Subject();

    const { url, params } = pluckSocketParams(state$.value);
    const ws = webSocket({
      url,
      openObserver: openEvents,
    });

    openEvents.subscribe(() => ws.next(params));

    const update$ = ws.pipe(
      filter(
        ({ MessageType, subMessageType }: any) => MessageType === MINILOBBY_PLAYERS_NUMBER_UPDATE
            || (MessageType === UPDATED_LOBBY_DATA
              && [
                UPDATED_TABLE_HISTORY,
                CANCEL_TABLE,
                INITIALIZE_TABLE,
                UPDATE_FREE_SEATS,
                TABLE_OCCUPANCY,
                SHUFFLE,
              ].includes(subMessageType))
      ),
      takeUntil(action$.pipe(filter(lobby.close.match)))
    );

    action$.pipe(filter(lobby.send.match), pluck('payload')).subscribe({
      next: (data) => ws.next(data),
    });

    action$
      .pipe(filter(lobby.close.match), first())
      .subscribe(() => ws.complete());

    return ws.pipe(
      filter(({ MessageType }: any) => MessageType === INITIAL_DATA),
      first(),
      switchMap((payload) => concat(
        of(payload),
        update$.pipe(
          buffer(update$.pipe(debounceTime(200))),
          map((updates) => ({ MessageType: UPDATED_LOBBY_DATA, updates })),
          takeUntil(action$.pipe(filter(lobby.close.match)))
        )
      )),
      catchError(onErrorEventHandler)
    );
  }),
  map((message) => mapLobbyData(state$.value, message)),
  map(lobby.patch),
  catchError(onErrorEventHandler)
);

const lobbyRedirectEpic = (
  action$: Observable<Action>,
  state$: StateObservable<RootState>
) => action$.pipe(
  /* eslint-disable camelcase */
  filter(lobby.redirect.match),
  pluck('payload'),
  switchMap((payload) => of(payload).pipe(
    tap(({ limit, table, seatIndex = {} }: any) => {
      const pathSegment = getAuthEndpointForExternalIntegration(
        table.gameId,
        externalGameTypesSelector(state$.value)
      );
      const query = buildQueryFromObject('?', {
        game_id: table.gameId,
        table_id: table.id,
        limit_id: limit.id,
        seatIndex,
        identifier: authSelector(state$.value)?.identifier,
        operatorId: userSelector(state$.value).operatorId,
        ...(!!pathSegment && {
          token: authSelector(state$.value)?.token,
          uid: userIdSelector(state$.value),
          language: currentLanguageSelector(state$.value)?.code,
          openTable: table.id,
          fromEzugiLobby: true,
        }),
      });

      redirectToGame(
        query,
        Boolean(VITE_IS_BUNDLE),
        table.gameId,
        pathSegment
      );
    }),
    catchError(onErrorEventHandler)
  )),
  ignoreElements()
  /* eslint-enable camelcase */
);

const navigationEpic = (
  action$: Observable<Action>,
  state$: StateObservable<RootState>
) => action$.pipe(
  /* eslint-disable camelcase */
  filter(lobby.navigate.match),
  pluck('payload'),
  switchMap((payload) => {
    const { limit, table, seatIndex = {} } = payload;
    const actions = [];
    const authParams = authSelector(state$.value);
    const openTableMethod = lobbyOpenTableMethod(state$.value);
    const isEvoWrapped = isEvoWrappedSelector(state$.value);

    if (!isEmpty(seatIndex)) {
      const user = userSelector(state$.value);
      const clientId = `${user.operatorId}|${user.uid}|${table.id}`;

      const reserveSeatMsg = {
        BrandID: 0,
        ClientId: clientId,
        PlayerId: `${user.uid}`,
        SeatId: seatIndex,
        ClientIP: `${user.clientIp}`,
        clientType: 'html',
        MessageType: MINILOBBY_RESERVE_SEAT,
        OperatorID: user.operatorId,
        TableId: `${table.id}`,
        Nickname: `${user.nickname}`,
        destination: 'table',
        gameType: table.gameId,
      };

      actions.push(lobby.send(reserveSeatMsg as any));
    }

    const isRedirectHandledByOperator = prop('postMessage', authParams) === '1'
        || openTableMethod === NAVIGATION_METHODS.POST
        || isEvoWrapped;

    if (isRedirectHandledByOperator) {
      actions.push(
        lobby.post({
          msg: POST_MESSAGES.OPEN_TABLE,
          table,
          limit,
          ...(!isEmpty(seatIndex)
            ? { seatIndex: seatIndex.replace('s', '') }
            : {}),
        } as any)
      );
    } else {
      // default method is NAVIGATION_METHODS.REPLACE
      actions.push(
        lobby.redirect({
          limit,
          table,
          ...(!isEmpty(seatIndex)
            ? { seatIndex: seatIndex.replace('s', '') }
            : {}),
        } as any)
      );
    }

    return concat(of(...actions));
  }),
  catchError(onErrorEventHandler)
  /* eslint-enable camelcase */
);

const lobbyPostMessage = (
  action$: Observable<Action>,
  state$: StateObservable<RootState>
) => action$.pipe(
  filter(lobby.post.match),
  switchMap(({ payload }: any) => {
    const state = state$.value;
    const postFn = getLobbyPostFn(payload);
    return from(postFn(payload, state)).pipe(
      map((ok) => ({ ok: Boolean(ok), message: '' })),
      catchError((e) => of({ ok: false, message: String(e.message) }))
    );
  }),
  flatMap(({ ok, message }: { ok: boolean; message: string }) => of(...(!ok ? [ notification.add({ message }) ] : [])))
);

const lobbyToggleFavorite = (
  action$: Observable<Action>,
  state$: StateObservable<RootState>
) => action$.pipe(
  filter(lobby.toggleFavorite.match),
  switchMap(({ payload }: any) => {
    const { id, favorite } = payload;

    const authGroups = lobbyAuthGroupsSelector(state$.value);
    const authParams = authSelector(state$.value) ?? { identifier: '' };

    const { identifier } = authParams;

    return from(
      setFavorite({
        // eslint-disable-next-line camelcase
        table_id: id,
        action: favorite ? REMOVE_FAVORITE : ADD_FAVORITE,
        identifier,
      })
    ).pipe(
      map(() => {
        const nextAuthGroups = updateAuthGroups(authGroups, Number(id), favorite);

        return lobby.patch({
          authGroups: nextAuthGroups,
        });
      }),
      catchError(() => [])
    );
  })
);

export default combineEpics(
  toggleEpic,
  openEpic,
  navigationEpic,
  lobbyRedirectEpic,
  lobbyPostMessage,
  lobbyToggleFavorite
);
